import apiClient from './apiClient';

export type PrescriptionStatus = 'draft' | 'active' | 'signed' | 'cancelled';

export type PrescriptionDisplayStatus =
  | 'draft'
  | 'issued'
  | 'partially_dispensed'
  | 'dispensed'
  | 'revoked'
  | 'expired';

export interface PrescriptionItem {
  productId: string;
  denumire: string;
  doza: string;
  frecventa: string;
  durata: string;
  observatii?: string;
}

export interface PrescriptionMedication {
  medicationId: string;
  medicationName?: string;
  commercialName?: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
  quantity: number;
  unit: string;
}

/** Răspuns API /prescriptions (personal clinică). */
export interface StaffPrescriptionRow {
  id: string;
  patient_id: string;
  clinic_id: string;
  visit_id?: string | null;
  veterinarian_id: string;
  status: string;
  display_status?: PrescriptionDisplayStatus;
  issued_at?: string | null;
  expires_at?: string | null;
  internal_notes?: string | null;
  patient_instructions?: string | null;
  created_at: string;
  updated_at: string;
  items?: StaffPrescriptionItemRow[];
  patient?: { id: string; name: string; species?: string; breed?: string } | null;
  veterinarian_name?: string;
  owner_name?: string;
  events?: unknown[];
  addendums?: Array<{
    id: string;
    body: string;
    client_visible?: boolean;
    created_at?: string;
    created_by?: string;
  }>;
}

export interface StaffPrescriptionItemRow {
  id: string;
  prescription_id: string;
  product_id?: string | null;
  medication_name: string;
  dosage: string;
  frequency?: string | null;
  duration?: string | null;
  instructions?: string | null;
  quantity_prescribed: number | string;
  quantity_dispensed?: number | string;
  unit: string;
  internal_line_notes?: string | null;
}

export interface Prescription {
  id: string;
  patient_id: string;
  clinic_id: string;
  doctor_id: string;
  visit_id?: string | null;
  items: Array<{
    id?: string;
    medication_name: string;
    dosage: string;
    frequency?: string;
    duration?: string;
    instructions?: string;
    quantity?: number;
    quantity_prescribed?: number;
    quantity_dispensed?: number;
    unit?: string;
    product_id?: string;
  }>;
  status: string;
  display_status?: PrescriptionDisplayStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
  patient?: {
    id: string;
    name: string;
    species?: string;
    breed?: string;
  };
  veterinarian_name?: string;
  owner_name?: string;
}

export interface CreatePrescriptionData {
  clinicId: string;
  petId: string;
  ownerId: string;
  doctorId?: string;
  veterinarianId?: string;
  visitId?: string;
  reason: string;
  notes?: string;
  items?: PrescriptionItem[];
  medications?: PrescriptionMedication[];
  validDays?: number;
  status: PrescriptionStatus;
}

export interface Medication {
  id: string;
  name: string;
  substance: string;
  concentration: string;
  form: string;
  commercialName: string;
  description?: string;
}

function mapStaffRowToLegacy(p: StaffPrescriptionRow): Prescription {
  const items = (p.items || []).map((it) => ({
    id: it.id,
    medication_name: it.medication_name,
    dosage: it.dosage,
    frequency: it.frequency || undefined,
    duration: it.duration || undefined,
    instructions: it.instructions || undefined,
    quantity: Number(it.quantity_prescribed) || 0,
    quantity_prescribed: Number(it.quantity_prescribed) || 0,
    quantity_dispensed: Number(it.quantity_dispensed) || 0,
    unit: it.unit,
    product_id: it.product_id || undefined,
  }));
  return {
    id: p.id,
    patient_id: p.patient_id,
    clinic_id: p.clinic_id,
    doctor_id: p.veterinarian_id,
    visit_id: p.visit_id,
    items,
    status: p.status === 'void' ? 'cancelled' : p.status,
    display_status: p.display_status,
    notes: p.internal_notes || undefined,
    created_at: p.created_at,
    updated_at: p.updated_at,
    patient: p.patient || undefined,
    veterinarian_name: p.veterinarian_name,
    owner_name: p.owner_name,
  };
}

class PrescriptionsService {
  private toCreateBody(prescriptionData: CreatePrescriptionData) {
    const meds = prescriptionData.medications || [];
    const fromItems = prescriptionData.items || [];

    const itemsFromMedications = meds.map((med) => ({
      product_id: med.medicationId || null,
      medication_name: med.medicationName || med.commercialName || 'Medicament',
      dosage: med.dosage,
      frequency: med.frequency || null,
      duration: med.duration || null,
      instructions: med.instructions || null,
      quantity_prescribed: med.quantity,
      unit: med.unit,
    }));

    const itemsFromLegacy = fromItems.map((item) => ({
      product_id: item.productId || null,
      medication_name: item.denumire,
      dosage: item.doza,
      frequency: item.frecventa || null,
      duration: item.durata || null,
      instructions: item.observatii || null,
      quantity_prescribed: 1,
      unit: 'buc',
    }));

    const items = itemsFromMedications.length > 0 ? itemsFromMedications : itemsFromLegacy;
    if (!items.length) {
      throw new Error('Cel putin un medicament este obligatoriu');
    }

    const issue_immediately =
      prescriptionData.status === 'active' || prescriptionData.status === 'signed';

    let expires_at: string | null = null;
    if (prescriptionData.validDays != null && prescriptionData.validDays > 0) {
      const d = new Date();
      d.setDate(d.getDate() + prescriptionData.validDays);
      expires_at = d.toISOString().slice(0, 10);
    }

    return {
      patient_id: prescriptionData.petId,
      clinic_id: prescriptionData.clinicId,
      visit_id: prescriptionData.visitId || null,
      veterinarian_id: prescriptionData.veterinarianId || prescriptionData.doctorId || undefined,
      expires_at,
      internal_notes: prescriptionData.notes || null,
      patient_instructions: prescriptionData.reason || null,
      issue_immediately,
      items,
    };
  }

  async createPrescription(prescriptionData: CreatePrescriptionData): Promise<Prescription> {
    const body = this.toCreateBody(prescriptionData);
    const response = await apiClient.post('/prescriptions', body);
    if (response.data.success) {
      return mapStaffRowToLegacy(response.data.data as StaffPrescriptionRow);
    }
    throw new Error(response.data.error || 'Eroare la crearea prescriptiei');
  }

  async getPrescription(id: string): Promise<StaffPrescriptionRow> {
    const response = await apiClient.get(`/prescriptions/${id}`);
    if (response.data.success) {
      return response.data.data as StaffPrescriptionRow;
    }
    throw new Error(response.data.error || 'Eroare');
  }

  async getPrescriptions(filters?: { patientId?: string; status?: string }): Promise<Prescription[]> {
    const params = new URLSearchParams();
    if (filters?.patientId) params.set('patient_id', filters.patientId);
    if (filters?.status && filters.status !== 'all') {
      const st =
        filters.status === 'cancelled' ? 'void' : filters.status === 'signed' ? 'signed' : filters.status;
      params.set('status', st);
    }
    const query = params.toString();
    const response = await apiClient.get(`/prescriptions${query ? `?${query}` : ''}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Eroare la listare');
    }
    const rows = (response.data.data || []) as StaffPrescriptionRow[];
    return rows.map(mapStaffRowToLegacy);
  }

  async getPrescriptionsForPet(petId: string): Promise<Prescription[]> {
    return this.getPrescriptions({ patientId: petId });
  }

  async updateDraft(
    id: string,
    body: {
      expires_at?: string | null;
      internal_notes?: string | null;
      patient_instructions?: string | null;
      items?: Array<{
        product_id?: string | null;
        medication_name: string;
        dosage: string;
        frequency?: string | null;
        duration?: string | null;
        instructions?: string | null;
        quantity_prescribed: number;
        unit: string;
        internal_line_notes?: string | null;
      }>;
    }
  ): Promise<StaffPrescriptionRow> {
    const response = await apiClient.patch(`/prescriptions/${id}`, body);
    if (response.data.success) {
      return response.data.data as StaffPrescriptionRow;
    }
    throw new Error(response.data.error || 'Eroare la actualizare');
  }

  async issuePrescription(id: string): Promise<StaffPrescriptionRow> {
    const response = await apiClient.post(`/prescriptions/${id}/issue`, {});
    if (response.data.success) {
      return response.data.data as StaffPrescriptionRow;
    }
    throw new Error(response.data.error || 'Eroare la emitere');
  }

  async revokePrescription(id: string, reason: string): Promise<StaffPrescriptionRow> {
    const response = await apiClient.post(`/prescriptions/${id}/revoke`, { reason });
    if (response.data.success) {
      return response.data.data as StaffPrescriptionRow;
    }
    throw new Error(response.data.error || 'Eroare la anulare');
  }

  async dispense(
    id: string,
    lines: Array<{ prescription_item_id: string; quantity: number; notes?: string }>
  ): Promise<StaffPrescriptionRow> {
    const response = await apiClient.post(`/prescriptions/${id}/dispense`, { lines });
    if (response.data.success) {
      return response.data.data as StaffPrescriptionRow;
    }
    throw new Error(response.data.error || 'Eroare la eliberare');
  }

  async addAddendum(
    id: string,
    body: { text: string; client_visible: boolean }
  ): Promise<StaffPrescriptionRow> {
    const response = await apiClient.post(`/prescriptions/${id}/addendum`, body);
    if (response.data.success) {
      return response.data.data as StaffPrescriptionRow;
    }
    throw new Error(response.data.error || 'Eroare addendum');
  }

  async getHistory(id: string): Promise<unknown[]> {
    const response = await apiClient.get(`/prescriptions/${id}/history`);
    if (response.data.success) {
      return response.data.data || [];
    }
    throw new Error(response.data.error || 'Eroare istoric');
  }

  async getMedications(): Promise<Medication[]> {
    const response = await apiClient.get('/vet-products?limit=100');
    const products = response.data?.data || [];
    return products.map((product: any) => ({
      id: product.id,
      name: product.commercial_name || product.name || 'Produs',
      substance: (product.active_substances || []).map((s: any) => s.name).join(', '),
      concentration: product.concentration || '',
      form: product.pharmaceutical_form || '',
      commercialName: product.commercial_name || product.name || 'Produs',
      description: product.description || '',
    }));
  }

  async checkAllergies(petId: string, substances: string[]): Promise<{ hasAllergies: boolean; allergies: string[] }> {
    const response = await apiClient.post(`/pets/${petId}/check-allergies`, {
      substances,
    });
    return response.data.data;
  }

  async getPetAllergies(petId: string): Promise<string[]> {
    const response = await apiClient.get(`/patients/${petId}`);
    const rawAllergies = response.data?.data?.allergies;
    if (Array.isArray(rawAllergies)) return rawAllergies;
    if (typeof rawAllergies === 'string') {
      try {
        const parsed = JSON.parse(rawAllergies);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  async checkAllergyConflict(petId: string, medicationName: string, medicationId?: string): Promise<boolean> {
    const allergies = (await this.getPetAllergies(petId)).map((a) => a.toLowerCase());
    if (allergies.length === 0) return false;

    if (medicationId) {
      const medResponse = await apiClient.get(`/vet-products/${medicationId}`);
      const product = medResponse.data?.data;
      const activeSubstances = (product?.active_substances || []).map((s: any) =>
        String(s.name || '').toLowerCase()
      );
      return activeSubstances.some((substance: string) =>
        allergies.some((allergy) => substance.includes(allergy) || allergy.includes(substance))
      );
    }

    const lowerName = medicationName.toLowerCase();
    return allergies.some((allergy) => lowerName.includes(allergy) || allergy.includes(lowerName));
  }

  async generatePrescriptionPDF(id: string): Promise<void> {
    const response = await apiClient.get(`/prescriptions/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `reteta_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const prescriptionsService = new PrescriptionsService();
